import { z } from 'zod';
import { ATTACHMENT_KIND, AUTOMATION_TRIGGER, BENEFIT_STATUS, BENEFIT_TYPE, DOCUMENT_CATEGORY, EVENT_TYPE, MATCH_STATUS, MEDIA_CHANNEL, MEDIA_KIND, MEDIA_POST_STATUS, ORG_UNIT_TYPE, RECURRENCE, SOURCE_OF_TRUTH, SPONSORSHIP_STATUS, SPONSOR_FURTHER_REQUESTS, SPONSOR_REQUEST_STATUS, SPONSOR_STATUS, STORAGE_PROVIDER, TASK_PRIORITY, TASK_STATUS, VISIBILITY, } from './enums.js';
import { ACTIONS, LEVELS, RESOURCES } from './permissions.js';
/* ------------------------------------------------------------------ *
 * Bausteine
 * ------------------------------------------------------------------ */
export const idSchema = z.string().min(1);
export const isoDateTimeSchema = z
    .string()
    .refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Ungültiges Datum' });
export const optionalIsoDateTime = isoDateTimeSchema.nullish();
const trimmed = (max) => z.string().trim().max(max);
const requiredText = (max, field = 'Pflichtfeld') => z.string().trim().min(1, field).max(max);
const emptyToNull = (schema) => z.preprocess((v) => (v === '' || v === undefined ? null : v), schema.nullable());
/**
 * Wahrheitswert aus einem Query-Parameter.
 *
 * `z.coerce.boolean()` waere hier falsch: es wertet jede nicht leere
 * Zeichenkette als `true` - also auch den String "false".
 */
export const booleanQuery = z.preprocess((value) => {
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value !== 0;
    if (typeof value === 'string') {
        const normalised = value.trim().toLowerCase();
        if (['1', 'true', 'yes', 'ja', 'on'].includes(normalised))
            return true;
        if (['0', 'false', 'no', 'nein', 'off', ''].includes(normalised))
            return false;
    }
    return value;
}, z.boolean());
export const paginationSchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/* ------------------------------------------------------------------ *
 * Auth
 * ------------------------------------------------------------------ */
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email('Bitte gültige E-Mail-Adresse eingeben'),
    password: z.string().min(1, 'Passwort erforderlich'),
});
export const requestPasswordResetSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
});
export const performPasswordResetSchema = z.object({
    token: z.string().min(10),
    password: z.string().min(8, 'Mindestens 8 Zeichen'),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8, 'Mindestens 8 Zeichen'),
});
export const impersonateSchema = z.object({
    personId: idSchema,
});
/* ------------------------------------------------------------------ *
 * Organisation
 * ------------------------------------------------------------------ */
export const createOrgUnitSchema = z.object({
    name: requiredText(120, 'Name erforderlich'),
    type: z.enum(ORG_UNIT_TYPE).default('SECTION'),
    parentId: emptyToNull(idSchema).optional(),
    sortOrder: z.coerce.number().int().default(0),
});
export const updateOrgUnitSchema = createOrgUnitSchema.partial().extend({
    isActive: z.boolean().optional(),
});
export const createPersonSchema = z.object({
    firstName: requiredText(80, 'Vorname erforderlich'),
    lastName: requiredText(80, 'Nachname erforderlich'),
    email: emptyToNull(z.string().trim().toLowerCase().email('Ungültige E-Mail')).optional(),
    phone: emptyToNull(trimmed(40)).optional(),
    avatarUrl: emptyToNull(trimmed(500)).optional(),
    notes: emptyToNull(trimmed(2000)).optional(),
    /** YYYY-MM-DD; leer oder `null` entfernt das Datum. */
    birthDate: z.preprocess((value) => (value === '' ? null : value), z.string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Geburtsdatum als JJJJ-MM-TT')
        .refine((value) => !Number.isNaN(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value, 'Ungültiges Geburtsdatum')
        .refine((value) => value <= new Date().toISOString().slice(0, 10), 'Geburtsdatum liegt in der Zukunft')
        .refine((value) => value >= '1900-01-01', 'Ungültiges Geburtsdatum')
        .nullable()).optional(),
    /** Spielernummer des Verbands; nur Ziffern, leer entfernt sie. */
    playerNumber: emptyToNull(z.string().trim().regex(/^\d{1,12}$/, 'Spielernummer: nur Ziffern')).optional(),
});
export const updatePersonSchema = createPersonSchema.partial().extend({
    active: z.boolean().optional(),
});
export const assignRoleSchema = z.object({
    personId: idSchema,
    roleId: idSchema,
    organisationUnitId: idSchema,
    inheritsToChildren: z.boolean().default(true),
    validFrom: optionalIsoDateTime,
    validUntil: optionalIsoDateTime,
});
export const updatePersonRoleSchema = z.object({
    roleId: idSchema.optional(),
    organisationUnitId: idSchema.optional(),
    inheritsToChildren: z.boolean().optional(),
    validFrom: optionalIsoDateTime,
    validUntil: optionalIsoDateTime,
});
/* ------------------------------------------------------------------ *
 * Berechtigungen
 * ------------------------------------------------------------------ */
/** Ausnahme fuer eine Person - gilt immer fuer den gesamten Verein. */
export const setPermissionExceptionSchema = z.object({
    level: z.enum(LEVELS),
    validUntil: optionalIsoDateTime,
    note: emptyToNull(trimmed(300)).optional(),
});
export const createRoleSchema = z.object({
    name: requiredText(80, 'Name erforderlich'),
    description: emptyToNull(trimmed(300)).optional(),
    /** Rechte einer bestehenden Funktion als Ausgangspunkt uebernehmen. */
    copyFromRoleId: emptyToNull(idSchema).optional(),
});
export const updateRoleSchema = z.object({
    name: requiredText(80, 'Name erforderlich').optional(),
    description: emptyToNull(trimmed(300)).optional(),
    archived: z.boolean().optional(),
});
export const setRoleLevelSchema = z.object({ level: z.enum(LEVELS) });
export const updatePermissionFeatureSchema = z.object({
    label: requiredText(80, 'Bezeichnung erforderlich').optional(),
    description: emptyToNull(trimmed(300)).optional(),
    confidential: z.boolean().optional(),
});
export const permissionCheckSchema = z.object({
    organisationUnitId: idSchema,
    resource: z.enum(RESOURCES),
    action: z.enum(ACTIONS),
});
/* ------------------------------------------------------------------ *
 * Aufgaben
 * ------------------------------------------------------------------ */
/** Verweis auf eine Datei aus der Mediathek oder aus Wissen & Dokumente. */
export const attachmentRefSchema = z.object({
    kind: z.enum(ATTACHMENT_KIND),
    refId: idSchema,
});
const attachmentListSchema = z.array(attachmentRefSchema).max(50, 'Höchstens 50 Anhänge');
export const createTaskSchema = z.object({
    title: requiredText(200, 'Titel erforderlich'),
    description: emptyToNull(trimmed(5000)).optional(),
    status: z.enum(TASK_STATUS).default('OPEN'),
    priority: z.enum(TASK_PRIORITY).default('NORMAL'),
    dueAt: optionalIsoDateTime,
    startAt: optionalIsoDateTime,
    organisationUnitIds: z.array(idSchema).min(1, 'Mindestens ein Bereich erforderlich'),
    assigneePersonIds: z.array(idSchema).default([]),
    tags: z.array(trimmed(40)).default([]),
    checklist: z.array(requiredText(200)).default([]),
    recurrence: z.enum(RECURRENCE).default('NONE'),
    dependsOnTaskIds: z.array(idSchema).default([]),
    attachments: attachmentListSchema.default([]),
});
export const updateTaskSchema = createTaskSchema
    .partial()
    .extend({ organisationUnitIds: z.array(idSchema).min(1).optional() });
export const taskFilterSchema = z.object({
    organisationUnitId: idSchema.optional(),
    includeChildren: booleanQuery.default(true),
    status: z.union([z.enum(TASK_STATUS), z.array(z.enum(TASK_STATUS))]).optional(),
    assigneePersonId: idSchema.optional(),
    mineOnly: booleanQuery.default(false),
    overdueOnly: booleanQuery.default(false),
    search: trimmed(120).optional(),
    tag: trimmed(40).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
export const createTaskCommentSchema = z.object({
    body: requiredText(4000, 'Kommentar darf nicht leer sein'),
});
export const createChecklistItemSchema = z.object({
    title: requiredText(200),
});
export const addTaskAttachmentSchema = attachmentRefSchema;
export const updateChecklistItemSchema = z.object({
    title: trimmed(200).optional(),
    isDone: z.boolean().optional(),
});
/* ------------------------------------------------------------------ *
 * Vorlagen / Workflows
 * ------------------------------------------------------------------ */
export const templateItemSchema = z.object({
    title: requiredText(200),
    description: emptyToNull(trimmed(2000)).optional(),
    offsetDays: z.coerce.number().int().min(-365).max(365).default(0),
    priority: z.enum(TASK_PRIORITY).default('NORMAL'),
    defaultRoleId: emptyToNull(idSchema).optional(),
    assigneePersonIds: z.array(idSchema).default([]),
    sortOrder: z.coerce.number().int().default(0),
    /** Punkte, die jede daraus erzeugte Aufgabe als Checkliste erhaelt. */
    checklist: z.array(requiredText(200)).max(100, 'Höchstens 100 Checklistenpunkte').default([]),
    attachments: attachmentListSchema.default([]),
});
export const createTaskTemplateSchema = z.object({
    name: requiredText(120),
    description: emptyToNull(trimmed(2000)).optional(),
    category: emptyToNull(trimmed(60)).optional(),
    items: z.array(templateItemSchema).min(1, 'Mindestens ein Schritt erforderlich'),
});
export const updateTaskTemplateSchema = createTaskTemplateSchema.partial();
export const applyTemplateSchema = z.object({
    templateId: idSchema,
    referenceDate: isoDateTimeSchema,
    organisationUnitIds: z.array(idSchema).min(1),
    assigneePersonIds: z.array(idSchema).default([]),
    titlePrefix: emptyToNull(trimmed(80)).optional(),
});
/* ------------------------------------------------------------------ *
 * Kalender
 * ------------------------------------------------------------------ */
export const createEventSchema = z
    .object({
    title: requiredText(200, 'Titel erforderlich'),
    description: emptyToNull(trimmed(4000)).optional(),
    eventType: z.enum(EVENT_TYPE).default('EVENT'),
    startAt: isoDateTimeSchema,
    endAt: optionalIsoDateTime,
    allDay: z.boolean().default(false),
    location: emptyToNull(trimmed(200)).optional(),
    // Ein Termin gehoert zu Bereichen, zu Personen oder zu beidem
    // (Spezifikation Abschnitt 11). Ohne Bereich ist er ein persoenlicher
    // Termin und nur fuer die genannten Personen sichtbar.
    organisationUnitIds: z.array(idSchema).default([]),
    personIds: z.array(idSchema).default([]),
    sourceOfTruth: z.enum(SOURCE_OF_TRUTH).default('UK_INFO'),
})
    .refine((v) => v.organisationUnitIds.length > 0 || v.personIds.length > 0, {
    message: 'Mindestens ein Bereich oder eine Person erforderlich',
    path: ['organisationUnitIds'],
})
    .refine((v) => !v.endAt || Date.parse(v.endAt) >= Date.parse(v.startAt), {
    message: 'Ende darf nicht vor dem Beginn liegen',
    path: ['endAt'],
});
export const updateEventSchema = z.object({
    title: requiredText(200).optional(),
    description: emptyToNull(trimmed(4000)).optional(),
    eventType: z.enum(EVENT_TYPE).optional(),
    startAt: isoDateTimeSchema.optional(),
    endAt: optionalIsoDateTime,
    allDay: z.boolean().optional(),
    location: emptyToNull(trimmed(200)).optional(),
    organisationUnitIds: z.array(idSchema).optional(),
    personIds: z.array(idSchema).optional(),
});
export const calendarEventTaskSchema = z.object({
    id: idSchema.optional(),
    title: requiredText(200, 'Aufgabentitel erforderlich'),
    dueAt: optionalIsoDateTime.nullable().optional(),
    assigneePersonIds: z.array(idSchema).default([]),
});
export const calendarEventTasksSchema = z.object({
    tasks: z.array(calendarEventTaskSchema).max(100).default([]),
    templateId: idSchema.nullable().optional(),
});
export const calendarRangeSchema = z.object({
    from: isoDateTimeSchema,
    to: isoDateTimeSchema,
    organisationUnitId: idSchema.optional(),
    includeChildren: booleanQuery.default(true),
    includeTasks: booleanQuery.default(true),
    eventTypes: z.union([z.enum(EVENT_TYPE), z.array(z.enum(EVENT_TYPE))]).optional(),
});
/* ------------------------------------------------------------------ *
 * Sponsoring
 * ------------------------------------------------------------------ */
export const createSponsorSchema = z.object({
    companyName: requiredText(160, 'Firmenname erforderlich'),
    website: emptyToNull(trimmed(300)).optional(),
    notes: emptyToNull(trimmed(4000)).optional(),
    status: z.enum(SPONSOR_STATUS).default('PROSPECT'),
    managedByPersonId: emptyToNull(idSchema).optional(),
    furtherRequests: z.enum(SPONSOR_FURTHER_REQUESTS).default('VIA_MANAGER'),
    openFor: emptyToNull(trimmed(300)).optional(),
    /** Kategorie ist freiwillig. */
    categoryId: emptyToNull(idSchema).optional(),
    /** Werbemoeglichkeiten, fuer die der Sponsor grundsaetzlich ansprechbar ist. */
    interestOfferIds: z.array(idSchema).max(100).default([]),
});
export const updateSponsorSchema = createSponsorSchema.partial();
export const sponsorCategorySchema = z.object({
    name: requiredText(60, 'Bezeichnung erforderlich'),
    sortOrder: z.coerce.number().int().min(0).max(1000).default(0),
});
export const updateSponsorCategorySchema = sponsorCategorySchema.partial().extend({
    archived: z.boolean().optional(),
});
export const sponsoringOfferSchema = z.object({
    title: requiredText(120, 'Bezeichnung erforderlich'),
    description: emptyToNull(trimmed(1000)).optional(),
    benefitType: z.enum(BENEFIT_TYPE).nullable().optional(),
    organisationUnitId: emptyToNull(idSchema).optional(),
    /** Wie oft die Moeglichkeit gleichzeitig vergeben werden kann, z. B. 20 Bandenplaetze. */
    capacity: z.coerce.number().int().min(1).max(500).default(1),
    sortOrder: z.coerce.number().int().min(0).max(10000).default(0),
});
export const updateSponsoringOfferSchema = sponsoringOfferSchema.partial().extend({
    archived: z.boolean().optional(),
});
export const createSponsorRequestSchema = z.object({
    sponsorId: idSchema,
    offerId: emptyToNull(idSchema).optional(),
    topic: requiredText(200, 'Bitte angeben, wofür angefragt wird'),
    organisationUnitId: emptyToNull(idSchema).optional(),
    status: z.enum(['PLANNED', 'REQUESTED']).default('REQUESTED'),
    requestedAt: optionalIsoDateTime,
});
export const updateSponsorRequestSchema = z.object({
    status: z.enum(SPONSOR_REQUEST_STATUS).optional(),
    topic: requiredText(200).optional(),
    offerId: emptyToNull(idSchema).optional(),
    resultNote: emptyToNull(trimmed(500)).optional(),
});
export const createSponsorContactSchema = z.object({
    firstName: requiredText(80),
    lastName: requiredText(80),
    email: emptyToNull(z.string().trim().toLowerCase().email()).optional(),
    phone: emptyToNull(trimmed(40)).optional(),
    role: emptyToNull(trimmed(80)).optional(),
});
export const benefitInputSchema = z
    .object({
    /**
     * Beim Bearbeiten mitsenden. Nur so bleiben Lieferfortschritt und eine
     * bereits erzeugte Aufgabe erhalten - ohne id gilt die Leistung als neu.
     */
    id: idSchema.optional(),
    type: z.enum(BENEFIT_TYPE),
    description: emptyToNull(trimmed(500)).optional(),
    quantity: z.coerce.number().int().min(1).default(1),
    deliveredCount: z.coerce.number().int().min(0).default(0),
    status: z.enum(BENEFIT_STATUS).default('OPEN'),
    dueAt: optionalIsoDateTime,
    /** Verknuepfung mit dem Angebotskatalog - daraus ergibt sich die Belegung. */
    offerId: emptyToNull(idSchema).optional(),
})
    .refine((v) => v.deliveredCount <= v.quantity, {
    message: 'Es koennen nicht mehr Leistungen erbracht als vereinbart sein',
    path: ['deliveredCount'],
});
export const createSponsorshipSchema = z
    .object({
    sponsorId: idSchema,
    title: requiredText(160, 'Bezeichnung erforderlich'),
    validFrom: isoDateTimeSchema,
    validUntil: isoDateTimeSchema,
    amount: z.coerce.number().min(0).default(0),
    currency: z.string().trim().length(3).default('EUR'),
    status: z.enum(SPONSORSHIP_STATUS).default('ACTIVE'),
    notes: emptyToNull(trimmed(4000)).optional(),
    organisationUnitIds: z.array(idSchema).min(1, 'Mindestens ein Bereich erforderlich'),
    benefits: z.array(benefitInputSchema).default([]),
})
    .refine((v) => Date.parse(v.validUntil) >= Date.parse(v.validFrom), {
    message: 'Vertragsende darf nicht vor dem Beginn liegen',
    path: ['validUntil'],
});
export const updateSponsorshipSchema = z.object({
    title: requiredText(160).optional(),
    validFrom: isoDateTimeSchema.optional(),
    validUntil: isoDateTimeSchema.optional(),
    amount: z.coerce.number().min(0).optional(),
    currency: z.string().trim().length(3).optional(),
    status: z.enum(SPONSORSHIP_STATUS).optional(),
    notes: emptyToNull(trimmed(4000)).optional(),
    organisationUnitIds: z.array(idSchema).min(1).optional(),
    benefits: z.array(benefitInputSchema).optional(),
});
export const updateBenefitSchema = z.object({
    status: z.enum(BENEFIT_STATUS).optional(),
    deliveredCount: z.coerce.number().int().min(0).optional(),
    dueAt: optionalIsoDateTime,
    description: emptyToNull(trimmed(500)).optional(),
});
/* ------------------------------------------------------------------ *
 * Dokumente / Wissen
 * ------------------------------------------------------------------ */
export const createDocumentSchema = z
    .object({
    title: requiredText(200, 'Titel erforderlich'),
    description: emptyToNull(trimmed(4000)).optional(),
    category: z.enum(DOCUMENT_CATEGORY).default('SONSTIGES'),
    storageProvider: z.enum(STORAGE_PROVIDER).default('EXTERNAL_LINK'),
    externalUrl: emptyToNull(trimmed(1000)).optional(),
    storageKey: emptyToNull(trimmed(500)).optional(),
    fileName: emptyToNull(trimmed(300)).optional(),
    mimeType: emptyToNull(trimmed(160)).optional(),
    sizeBytes: z.coerce.number().int().min(0).nullish(),
    visibility: z.enum(VISIBILITY).default('UNIT'),
    organisationUnitIds: z.array(idSchema).min(1, 'Mindestens ein Bereich erforderlich'),
    tags: z.array(trimmed(40)).default([]),
})
    .refine((v) => v.storageProvider !== 'EXTERNAL_LINK' || !!v.externalUrl, {
    message: 'Link erforderlich',
    path: ['externalUrl'],
});
export const updateDocumentSchema = z.object({
    title: requiredText(200).optional(),
    description: emptyToNull(trimmed(4000)).optional(),
    category: z.enum(DOCUMENT_CATEGORY).optional(),
    externalUrl: emptyToNull(trimmed(1000)).optional(),
    visibility: z.enum(VISIBILITY).optional(),
    organisationUnitIds: z.array(idSchema).min(1).optional(),
    tags: z.array(trimmed(40)).optional(),
});
/* ------------------------------------------------------------------ *
 * Medien
 * ------------------------------------------------------------------ */
export const createMediaAssetSchema = z
    .object({
    title: requiredText(200, 'Titel erforderlich'),
    kind: z.enum(MEDIA_KIND).default('PHOTO'),
    storageProvider: z.enum(STORAGE_PROVIDER).default('EXTERNAL_LINK'),
    externalUrl: emptyToNull(trimmed(1000)).optional(),
    storageKey: emptyToNull(trimmed(500)).optional(),
    fileName: emptyToNull(trimmed(300)).optional(),
    mimeType: emptyToNull(trimmed(160)).optional(),
    sizeBytes: z.coerce.number().int().min(0).nullish(),
    organisationUnitIds: z.array(idSchema).min(1, 'Mindestens ein Bereich erforderlich'),
    tags: z.array(trimmed(40)).default([]),
})
    .refine((v) => v.storageProvider !== 'EXTERNAL_LINK' || !!v.externalUrl, {
    message: 'Link erforderlich',
    path: ['externalUrl'],
});
/**
 * Aenderbare Metadaten eines Mediums. Technische Speicherfelder werden nie
 * direkt aus JSON uebernommen; eine lokale Datei wird ueber den geschuetzten
 * Ersetzen-Endpunkt ausgetauscht.
 */
export const updateMediaAssetSchema = z
    .object({
    title: requiredText(200, 'Titel erforderlich').optional(),
    kind: z.enum(MEDIA_KIND).optional(),
    storageProvider: z.literal('EXTERNAL_LINK').optional(),
    externalUrl: emptyToNull(trimmed(1000)).optional(),
    organisationUnitIds: z.array(idSchema).min(1, 'Mindestens ein Bereich erforderlich').optional(),
    tags: z.array(trimmed(40)).optional(),
})
    .superRefine((value, issue) => {
    if (value.storageProvider === 'EXTERNAL_LINK' && !value.externalUrl) {
        issue.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Link erforderlich',
            path: ['externalUrl'],
        });
    }
});
export const createMediaPostSchema = z.object({
    title: requiredText(200, 'Titel erforderlich'),
    body: emptyToNull(trimmed(8000)).optional(),
    status: z.enum(MEDIA_POST_STATUS).default('IDEA'),
    plannedAt: optionalIsoDateTime,
    responsiblePersonId: emptyToNull(idSchema).optional(),
    approverPersonId: emptyToNull(idSchema).optional(),
    matchId: emptyToNull(idSchema).optional(),
    channels: z.array(z.enum(MEDIA_CHANNEL)).default([]),
    organisationUnitIds: z.array(idSchema).min(1, 'Mindestens ein Bereich erforderlich'),
    assetIds: z.array(idSchema).default([]),
});
export const updateMediaPostSchema = createMediaPostSchema
    .partial()
    .extend({ organisationUnitIds: z.array(idSchema).min(1).optional() });
export const mediaPostTransitionSchema = z.object({
    status: z.enum(MEDIA_POST_STATUS),
    note: emptyToNull(trimmed(500)).optional(),
});
/* ------------------------------------------------------------------ *
 * Spiele / Fussball
 * ------------------------------------------------------------------ */
export const createMatchSchema = z.object({
    organisationUnitId: idSchema,
    teamId: idSchema.optional(),
    homeTeam: requiredText(120),
    awayTeam: requiredText(120),
    startAt: isoDateTimeSchema,
    location: emptyToNull(trimmed(200)).optional(),
    competition: emptyToNull(trimmed(160)).optional(),
    isHome: z.boolean().default(true),
    status: z.enum(MATCH_STATUS).default('SCHEDULED'),
    homeScore: z.coerce.number().int().min(0).nullish(),
    awayScore: z.coerce.number().int().min(0).nullish(),
    sourceOfTruth: z.enum(SOURCE_OF_TRUTH).default('UK_INFO'),
});
export const updateMatchSchema = createMatchSchema.partial();
export const linkExternalPlayerSchema = z.object({
    personId: emptyToNull(idSchema),
});
export const syncRequestSchema = z.object({
    organisationUnitId: idSchema.optional(),
    dryRun: z.boolean().default(false),
});
/* ------------------------------------------------------------------ *
 * Google Kalender
 * ------------------------------------------------------------------ */
export const connectGoogleSchema = z.object({
    displayName: requiredText(120),
    accountRef: requiredText(160),
});
export const linkExternalCalendarSchema = z.object({
    externalCalendarId: requiredText(200),
    name: requiredText(160),
    direction: z.enum(['IMPORT', 'EXPORT', 'BIDIRECTIONAL']).default('BIDIRECTIONAL'),
});
/* ------------------------------------------------------------------ *
 * Automatisierung
 * ------------------------------------------------------------------ */
export const upsertAutomationRuleSchema = z.object({
    trigger: z.enum(AUTOMATION_TRIGGER),
    organisationUnitId: emptyToNull(idSchema).optional(),
    templateId: emptyToNull(idSchema).optional(),
    isActive: z.boolean().default(true),
    config: z
        .object({
        onlyHomeMatches: z.boolean().default(true),
        createMediaPost: z.boolean().default(true),
        checkSponsoring: z.boolean().default(true),
        leadDays: z.coerce.number().int().min(0).max(365).default(30),
    })
        .default({
        onlyHomeMatches: true,
        createMediaPost: true,
        checkSponsoring: true,
        leadDays: 30,
    }),
});
/* ------------------------------------------------------------------ *
 * Suche / Benachrichtigungen
 * ------------------------------------------------------------------ */
export const searchSchema = z.object({
    q: z.string().trim().min(2, 'Mindestens 2 Zeichen'),
    organisationUnitId: idSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const notificationSettingSchema = z.object({
    type: z.string().min(1),
    inApp: z.boolean(),
    email: z.boolean(),
    // Aeltere Oberflaechen schicken das Feld nicht mit.
    push: z.boolean().default(true),
});
/**
 * Grenzen der Betragsklassen in Cent.
 * Dass die untere unter der oberen liegen muss, prueft der Fachdienst - dort
 * laesst sich der Grund in einem Satz erklaeren.
 */
export const financeThresholdsSchema = z.object({
    mediumCents: z.coerce.number().int().min(0).max(100_000_000),
    highCents: z.coerce.number().int().min(1).max(100_000_000),
});
/* ------------------------------------------------------------------ *
 * Benutzerverwaltung
 * ------------------------------------------------------------------ */
export const createUserSchema = z.object({
    personId: idSchema,
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(8, 'Mindestens 8 Zeichen'),
    isSuperadmin: z.boolean().default(false),
});
export const updateUserSchema = z.object({
    isActive: z.boolean().optional(),
    isSuperadmin: z.boolean().optional(),
    password: z.string().min(8).optional(),
});
/* ------------------------------------------------------------------ *
 * Seitenweise Listen
 * ------------------------------------------------------------------ */
/**
 * Einheitliche Blaetterung fuer lange Listen.
 *
 * Seiten zaehlen ab 1 - so steht es auch in der Oberflaeche. Die Obergrenze
 * verhindert, dass ein einzelner Abruf die halbe Datenbank zieht.
 */
export const pageQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
/** Schneidet eine bereits geladene Liste auf eine Seite zu. */
export function paginate(items, query) {
    const start = (query.page - 1) * query.pageSize;
    return {
        items: items.slice(start, start + query.pageSize),
        total: items.length,
        page: query.page,
        pageSize: query.pageSize,
    };
}
/** Personenimport aus einer CSV-Datei. */
export const importPersonsSchema = z.object({
    csv: z.string().min(1).max(2_000_000),
    // Vorgabe ist die Probe: erst ansehen, dann uebernehmen.
    dryRun: z.boolean().default(true),
});
//# sourceMappingURL=schemas.js.map